import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import type { LegacyElastics } from '@odo/shared-ui/clinical/elastics/model';

/** Whether an elastic is present on a side (label_4 column options). */
const ELASTIC_OPTIONS: readonly string[] = ['Yes', 'No', 'Multiple, See Note', 'Future'];

// Mirrors ortho_structure.json - "Elast 1 Left" column options.
const LEFT_ELASTIC_SIZE_OPTIONS: readonly string[] = [
    '3/16 4-4.5oz Med',
    '3/16 6-6.5oz Heavy',
    '5/16 4-4.5oz Med',
    '5/16 6-6.5oz Heavy',
    '1/8" 6-6.5 oz',
    '3/16 8oz',
    '5/16 8oz',
    'n/a',
    'Multiple'
];

// Mirrors ortho_structure.json - "Elast 2 Right" column options. Differs from
// LEFT: "1/8\" 6.5 oz" (vs "6-6.5 oz") and the extra "Both 3/16\" 4.5 and 6.5oz".
const RIGHT_ELASTIC_SIZE_OPTIONS: readonly string[] = [
    '3/16 4-4.5oz Med',
    '3/16 6-6.5oz Heavy',
    '5/16 4-4.5oz Med',
    '5/16 6-6.5oz Heavy',
    '1/8" 6.5 oz',
    'Both 3/16" 4.5 and 6.5oz',
    '3/16 8oz',
    '5/16 8oz',
    'n/a',
    'Multiple'
];

const EMPTY_LEGACY: LegacyElastics = {
    leftElastic: '',
    rightElastic: '',
    leftElasticSize: '',
    rightElasticSize: ''
};

interface LegacyElasticsFormProps {
    value: LegacyElastics | undefined;
    onChange: (next: LegacyElastics) => void;
    readOnly?: boolean;
}

interface SelectFieldProps {
    label: string;
    value: string;
    options: readonly string[];
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

function SelectField({ label, value, options, onChange, placeholder, disabled }: SelectFieldProps) {
    // Show a stored value that is no longer in the option list (e.g. old data)
    // so it isn't silently lost.
    const showOrphan = value.length > 0 && !options.includes(value);
    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-700">{label}</label>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    className={`${baseFieldClasses} appearance-none h-9 pr-9 ${value ? '' : 'text-gray-400'} disabled:opacity-60`}
                >
                    <option value="" disabled>
                        {placeholder ?? 'Select…'}
                    </option>
                    {showOrphan && (
                        <option value={value} className="text-gray-800">
                            {value}
                        </option>
                    )}
                    {options.map((opt) => (
                        <option key={opt} value={opt} className="text-gray-800">
                            {opt}
                        </option>
                    ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
        </div>
    );
}

// Pre-odontogram Left/Right elastics form for older records; "No" on a side sets its size
// to "n/a", as the original form did.
export function LegacyElasticsForm({ value, onChange, readOnly = false }: LegacyElasticsFormProps) {
    const v = value ?? EMPTY_LEGACY;

    const set = (patch: Partial<LegacyElastics>) => onChange({ ...v, ...patch });

    const handleElastic = (side: 'left' | 'right', next: string) => {
        if (side === 'left') {
            set(
                next === 'No'
                    ? { leftElastic: next, leftElasticSize: 'n/a' }
                    : { leftElastic: next }
            );
        } else {
            set(
                next === 'No'
                    ? { rightElastic: next, rightElasticSize: 'n/a' }
                    : { rightElastic: next }
            );
        }
    };

    return (
        <div className="grid grid-cols-2 gap-3">
            <SelectField
                label="Left Elastic"
                value={v.leftElastic}
                onChange={(next) => handleElastic('left', next)}
                options={ELASTIC_OPTIONS}
                placeholder="Select…"
                disabled={readOnly}
            />
            <SelectField
                label="Right Elastic"
                value={v.rightElastic}
                onChange={(next) => handleElastic('right', next)}
                options={ELASTIC_OPTIONS}
                placeholder="Select…"
                disabled={readOnly}
            />
            <SelectField
                label="Left Elastic Size"
                value={v.leftElasticSize}
                onChange={(next) => set({ leftElasticSize: next })}
                options={LEFT_ELASTIC_SIZE_OPTIONS}
                placeholder="Select size…"
                disabled={readOnly}
            />
            <SelectField
                label="Right Elastic Size"
                value={v.rightElasticSize}
                onChange={(next) => set({ rightElasticSize: next })}
                options={RIGHT_ELASTIC_SIZE_OPTIONS}
                placeholder="Select size…"
                disabled={readOnly}
            />
        </div>
    );
}
